import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tabs,
  Tab,
  TextField,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  Tooltip
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Info as InfoIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { format, differenceInDays, subDays } from 'date-fns';
import { collection, query, getDocs, orderBy as firestoreOrderBy, limit, where, Timestamp } from 'firebase/firestore';
import { db } from '../../../shared/utils/firebase';
import { toDate, isValidDate as isValidDateHelper } from '../../../shared/utils/dateHelpers';

// Types for our component
interface AuditLog {
  id: string;
  timestamp: number;
  action: string;
  actionType: string;
  userId: string;
  userName: string;
  userRole: string;
  details: string;
  clientId?: string;
  clientName?: string;
  metadata?: Record<string, any>;
}

interface UserActivitySummary {
  userId: string;
  userName: string;
  userRole: string;
  lastActivity: number;
  daysWithActivity: number;
  totalActions: number;
  actionsByType: Record<string, number>;
}

interface AuditLogsProps {
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setSnackbar: (snackbar: { open: boolean; message: string; type: 'success' | 'error' }) => void;
}

// Helper function to validate dates (using our utility)
const isValidDate = (dateValue: any): boolean => {
  return isValidDateHelper(dateValue);
};

/**
 * AuditLogs component displays audit logs and user activity data
 * for administrators to monitor system usage.
 */
const AuditLogs: React.FC<AuditLogsProps> = ({ setLoading, setError, setSnackbar }) => {
  // State for audit logs
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<AuditLog[]>([]);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [actionTypeFilter, setActionTypeFilter] = useState('all');
  const [userNameFilter, setUserNameFilter] = useState('all');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  
  // Sorting state (only used for fetching from Firestore)
  const [orderDirection] = useState<'asc' | 'desc'>('desc');
  
  // Tab state
  const [activeTab, setActiveTab] = useState('logs');
  
  // Summary state
  const [userSummaries, setUserSummaries] = useState<UserActivitySummary[]>([]);
  const [summaryOrderBy, setSummaryOrderBy] = useState('lastActivity');
  const [summaryOrderDirection, setSummaryOrderDirection] = useState<'asc' | 'desc'>('desc');
  
  // Available filter options (populated from data)
  const [availableActionTypes, setAvailableActionTypes] = useState<{id: string, name: string}[]>([]);
  const [availableUserNames, setAvailableUserNames] = useState<{id: string, name: string}[]>([]);
  const [availableUserRoles, setAvailableUserRoles] = useState<{id: string, name: string}[]>([]);

  // Calculate user activity summaries
  const calculateUserSummaries = useCallback((logs: AuditLog[]) => {
    const userMap = new Map<string, UserActivitySummary>();
    const userDailyActivity = new Map<string, Set<string>>();
    
    logs.forEach(log => {
      const userId = log.userId;
      const dateKey = isValidDate(log.timestamp) 
        ? format(new Date(log.timestamp), 'yyyy-MM-dd')
        : null;
      
      if (!userMap.has(userId)) {
        userMap.set(userId, {
          userId,
          userName: log.userName,
          userRole: log.userRole,
          lastActivity: log.timestamp,
          daysWithActivity: 0,
          totalActions: 0,
          actionsByType: {}
        });
        userDailyActivity.set(userId, new Set());
      }
      
      const summary = userMap.get(userId)!;
      const dailySet = userDailyActivity.get(userId)!;
      
      // Update last activity
      if (log.timestamp > summary.lastActivity) {
        summary.lastActivity = log.timestamp;
      }
      
      // Track unique days
      if (dateKey) {
        dailySet.add(dateKey);
      }
      
      // Count total actions
      summary.totalActions++;
      
      // Count actions by type
      if (!summary.actionsByType[log.actionType]) {
        summary.actionsByType[log.actionType] = 0;
      }
      summary.actionsByType[log.actionType]++;
    });
    
    // Convert map to array and calculate days with activity
    const summaries = Array.from(userMap.values()).map(summary => {
      const dailySet = userDailyActivity.get(summary.userId)!;
      return {
        ...summary,
        daysWithActivity: dailySet.size
      };
    });
    
    // Sort by last activity by default
    summaries.sort((a, b) => b.lastActivity - a.lastActivity);
    
    setUserSummaries(summaries);
  }, []);

  // Calculate user activity summaries with all users included
  const calculateUserSummariesWithAllUsers = useCallback((logs: AuditLog[], allUsersMap: Map<string, { userId: string; userName: string; userRole: string }>) => {
    const userMap = new Map<string, UserActivitySummary>();
    const userDailyActivity = new Map<string, Set<string>>();
    
    // First, initialize all users with zero activity
    allUsersMap.forEach((userData, userId) => {
      userMap.set(userId, {
        userId,
        userName: userData.userName,
        userRole: userData.userRole,
        lastActivity: 0, // Will be updated if user has activity
        daysWithActivity: 0,
        totalActions: 0,
        actionsByType: {}
      });
      userDailyActivity.set(userId, new Set());
    });
    
    // Then, update with actual activity from logs
    logs.forEach(log => {
      const userId = log.userId;
      const dateKey = isValidDate(log.timestamp) 
        ? format(new Date(log.timestamp), 'yyyy-MM-dd')
        : null;
      
      // Get existing summary or create if somehow missing
      let summary = userMap.get(userId);
      if (!summary) {
        summary = {
          userId,
          userName: log.userName,
          userRole: log.userRole,
          lastActivity: log.timestamp,
          daysWithActivity: 0,
          totalActions: 0,
          actionsByType: {}
        };
        userMap.set(userId, summary);
        userDailyActivity.set(userId, new Set());
      }
      
      const dailySet = userDailyActivity.get(userId)!;
      
      // Update last activity
      if (log.timestamp > summary.lastActivity) {
        summary.lastActivity = log.timestamp;
      }
      
      // Track unique days
      if (dateKey) {
        dailySet.add(dateKey);
      }
      
      // Count total actions
      summary.totalActions++;
      
      // Count actions by type
      if (!summary.actionsByType[log.actionType]) {
        summary.actionsByType[log.actionType] = 0;
      }
      summary.actionsByType[log.actionType]++;
    });
    
    // Convert map to array and calculate days with activity
    const summaries = Array.from(userMap.values()).map(summary => {
      const dailySet = userDailyActivity.get(summary.userId)!;
      return {
        ...summary,
        daysWithActivity: dailySet.size
      };
    });
    
    // Sort by last activity by default (users with no activity will be at the end)
    summaries.sort((a, b) => b.lastActivity - a.lastActivity);
    
    setUserSummaries(summaries);
  }, []);

  // Calculate user activity summaries with all users and their last activity
  const calculateUserSummariesWithAllUsersAndActivity = useCallback((logs: AuditLog[], allUsersMap: Map<string, { userId: string; userName: string; userRole: string; lastActivity: number }>) => {
    const userMap = new Map<string, UserActivitySummary>();
    const userDailyActivity = new Map<string, Set<string>>();
    
    // First, initialize all users with their known last activity
    allUsersMap.forEach((userData, userId) => {
      userMap.set(userId, {
        userId,
        userName: userData.userName,
        userRole: userData.userRole,
        lastActivity: userData.lastActivity, // Use the pre-fetched last activity
        daysWithActivity: 0,
        totalActions: 0,
        actionsByType: {}
      });
      userDailyActivity.set(userId, new Set());
    });
    
    // Then, update with actual activity from the 30-day logs
    logs.forEach(log => {
      const userId = log.userId;
      const dateKey = isValidDate(log.timestamp) 
        ? format(new Date(log.timestamp), 'yyyy-MM-dd')
        : null;
      
      // Get existing summary
      let summary = userMap.get(userId);
      if (!summary) {
        // User not in users collection, create entry
        summary = {
          userId,
          userName: log.userName,
          userRole: log.userRole,
          lastActivity: log.timestamp,
          daysWithActivity: 0,
          totalActions: 0,
          actionsByType: {}
        };
        userMap.set(userId, summary);
        userDailyActivity.set(userId, new Set());
      }
      
      const dailySet = userDailyActivity.get(userId)!;
      
      // Track unique days (only for the 30-day window)
      if (dateKey) {
        dailySet.add(dateKey);
      }
      
      // Count total actions (only for the 30-day window)
      summary.totalActions++;
      
      // Count actions by type (only for the 30-day window)
      if (!summary.actionsByType[log.actionType]) {
        summary.actionsByType[log.actionType] = 0;
      }
      summary.actionsByType[log.actionType]++;
    });
    
    // Convert map to array and calculate days with activity
    const summaries = Array.from(userMap.values()).map(summary => {
      const dailySet = userDailyActivity.get(summary.userId)!;
      return {
        ...summary,
        daysWithActivity: dailySet.size
      };
    });
    
    // Sort by last activity by default
    summaries.sort((a, b) => b.lastActivity - a.lastActivity);
    
    setUserSummaries(summaries);
  }, []);

  // Extract filter options from logs
  const extractFilterOptions = useCallback((logs: AuditLog[]) => {
    // Extract unique action types
    const actionTypesMap = new Map<string, {id: string, name: string}>();
    actionTypesMap.set('all', {id: 'all', name: 'All Action Types'});
    
    // Extract unique user names
    const userNamesMap = new Map<string, {id: string, name: string}>();
    userNamesMap.set('all', {id: 'all', name: 'All Users'});
    
    // Extract unique user roles
    const userRolesMap = new Map<string, {id: string, name: string}>();
    userRolesMap.set('all', {id: 'all', name: 'All Roles'});
    
    logs.forEach(log => {
      // Add action type if it doesn't exist
      if (log.actionType && !actionTypesMap.has(log.actionType)) {
        actionTypesMap.set(log.actionType, {
          id: log.actionType,
          name: log.actionType
        });
      }
      
      // Add user name if it doesn't exist
      if (log.userId && !userNamesMap.has(log.userId)) {
        userNamesMap.set(log.userId, {
          id: log.userId,
          name: log.userName || 'Unknown'
        });
      }
      
      // Add user role if it doesn't exist
      if (log.userRole && !userRolesMap.has(log.userRole)) {
        userRolesMap.set(log.userRole, {
          id: log.userRole,
          name: log.userRole
        });
      }
    });
    
    // Convert maps to sorted arrays
    const actionTypes = Array.from(actionTypesMap.values()).sort((a, b) => {
      if (a.id === 'all') return -1;
      if (b.id === 'all') return 1;
      return a.name.localeCompare(b.name);
    });
    
    const userNames = Array.from(userNamesMap.values()).sort((a, b) => {
      if (a.id === 'all') return -1;
      if (b.id === 'all') return 1;
      return a.name.localeCompare(b.name);
    });
    
    const userRoles = Array.from(userRolesMap.values()).sort((a, b) => {
      if (a.id === 'all') return -1;
      if (b.id === 'all') return 1;
      return a.name.localeCompare(b.name);
    });
    
    setAvailableActionTypes(actionTypes);
    setAvailableUserNames(userNames);
    setAvailableUserRoles(userRoles);
  }, []);

  // Extract filter options including all users
  const extractFilterOptionsWithAllUsers = useCallback((logs: AuditLog[], allUsersMap: Map<string, { userId: string; userName: string; userRole: string }>) => {
    // Extract unique action types
    const actionTypesMap = new Map<string, {id: string, name: string}>();
    actionTypesMap.set('all', {id: 'all', name: 'All Action Types'});
    
    // Extract unique user names - include ALL users
    const userNamesMap = new Map<string, {id: string, name: string}>();
    userNamesMap.set('all', {id: 'all', name: 'All Users'});
    
    // Add all users from the complete user map
    allUsersMap.forEach((userData, userId) => {
      userNamesMap.set(userId, {
        id: userId,
        name: userData.userName || 'Unknown'
      });
    });
    
    // Extract unique user roles
    const userRolesMap = new Map<string, {id: string, name: string}>();
    userRolesMap.set('all', {id: 'all', name: 'All Roles'});
    
    // Add roles from all users
    allUsersMap.forEach((userData) => {
      if (userData.userRole && !userRolesMap.has(userData.userRole)) {
        userRolesMap.set(userData.userRole, {
          id: userData.userRole,
          name: userData.userRole
        });
      }
    });
    
    // Also extract from logs for action types and any missing data
    logs.forEach(log => {
      // Add action type if it doesn't exist
      if (log.actionType && !actionTypesMap.has(log.actionType)) {
        actionTypesMap.set(log.actionType, {
          id: log.actionType,
          name: log.actionType
        });
      }
      
      // Update user name if it exists in logs with better data
      if (log.userId && userNamesMap.has(log.userId) && log.userName && log.userName !== 'Unknown') {
        userNamesMap.set(log.userId, {
          id: log.userId,
          name: log.userName
        });
      }
      
      // Add user role if it doesn't exist
      if (log.userRole && !userRolesMap.has(log.userRole)) {
        userRolesMap.set(log.userRole, {
          id: log.userRole,
          name: log.userRole
        });
      }
    });
    
    // Convert maps to sorted arrays
    const actionTypes = Array.from(actionTypesMap.values()).sort((a, b) => {
      if (a.id === 'all') return -1;
      if (b.id === 'all') return 1;
      return a.name.localeCompare(b.name);
    });
    
    const userNames = Array.from(userNamesMap.values()).sort((a, b) => {
      if (a.id === 'all') return -1;
      if (b.id === 'all') return 1;
      return a.name.localeCompare(b.name);
    });
    
    const userRoles = Array.from(userRolesMap.values()).sort((a, b) => {
      if (a.id === 'all') return -1;
      if (b.id === 'all') return 1;
      return a.name.localeCompare(b.name);
    });
    
    setAvailableActionTypes(actionTypes);
    setAvailableUserNames(userNames);
    setAvailableUserRoles(userRoles);
  }, []);

  // Apply filters to logs
  const applyFilters = useCallback((logsToFilter: AuditLog[]) => {
    let filtered = [...logsToFilter];
    
    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(log => 
        (log.action?.toLowerCase() || '').includes(searchLower) ||
        (log.details?.toLowerCase() || '').includes(searchLower) ||
        (log.userName?.toLowerCase() || '').includes(searchLower) ||
        (log.clientName?.toLowerCase() || '').includes(searchLower)
      );
    }
    
    // Filter by action type
    if (actionTypeFilter !== 'all') {
      filtered = filtered.filter(log => log.actionType === actionTypeFilter);
    }
    
    // Filter by user name
    if (userNameFilter !== 'all') {
      filtered = filtered.filter(log => log.userId === userNameFilter);
    }
    
    // Filter by user role
    if (userRoleFilter !== 'all') {
      filtered = filtered.filter(log => log.userRole === userRoleFilter);
    }
    
    // Store current filters in sessionStorage
    try {
      sessionStorage.setItem('auditLogs_filters', JSON.stringify({
        searchTerm,
        actionTypeFilter,
        userNameFilter,
        userRoleFilter
      }));
    } catch (error) {
      console.error('Error storing filters in sessionStorage:', error);
    }
    
    // Update filtered logs
    setFilteredLogs(filtered);
  }, [searchTerm, actionTypeFilter, userNameFilter, userRoleFilter]);

  // Function to fetch all users with their latest activity
  const fetchAllUsersWithLatestActivity = useCallback(async (): Promise<Map<string, { userId: string; userName: string; userRole: string; lastActivity: number }>> => {
    const userMap = new Map<string, { userId: string; userName: string; userRole: string; lastActivity: number }>();
    
    try {
      // First, get all users from the users collection
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      
      // Initialize map with all users
      usersSnapshot.docs.forEach(doc => {
        const data = doc.data();
        userMap.set(doc.id, {
          userId: doc.id,
          userName: data.displayName || data.email || 'Unknown',
          userRole: data.role || 'Unknown',
          lastActivity: 0 // Will be updated with actual activity
        });
      });
      
      // Now fetch the most recent activity for each user
      // This approach requires fewer reads than querying each user individually
      const thirtyDaysAgo = subDays(new Date(), 30);
      const auditLogsRef = collection(db, 'audit_logs');
      
      // Get recent logs to extract latest activity
      const recentLogsQuery = query(
        auditLogsRef,
        where('timestamp', '>=', Timestamp.fromDate(thirtyDaysAgo)),
        firestoreOrderBy('timestamp', 'desc'),
        limit(5000) // Get enough to likely include all active users
      );
      
      const recentSnapshot = await getDocs(recentLogsQuery);
      
      // Update last activity for users found in recent logs
      recentSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const userId = data.userId;
        const timestamp = toDate(data.timestamp)?.getTime() || 0;
        
        if (userId && userMap.has(userId)) {
          const userData = userMap.get(userId)!;
          if (timestamp > userData.lastActivity) {
            userData.lastActivity = timestamp;
          }
        }
      });
      
      // For users with no recent activity, we need to query their last log entry
      // We'll do this in batches to be efficient
      const usersWithNoRecentActivity = Array.from(userMap.entries())
        .filter(([_, userData]) => userData.lastActivity === 0)
        .map(([userId, _]) => userId);
      
      if (usersWithNoRecentActivity.length > 0) {
        // Query for the last activity of inactive users (in batches of 10)
        const batchSize = 10;
        for (let i = 0; i < usersWithNoRecentActivity.length; i += batchSize) {
          const batch = usersWithNoRecentActivity.slice(i, i + batchSize);
          
          const promises = batch.map(async (userId) => {
            const userLogsQuery = query(
              auditLogsRef,
              where('userId', '==', userId),
              firestoreOrderBy('timestamp', 'desc'),
              limit(1)
            );
            
            try {
              const snapshot = await getDocs(userLogsQuery);
              if (!snapshot.empty) {
                const data = snapshot.docs[0].data();
                const userData = userMap.get(userId);
                if (userData) {
                  userData.lastActivity = toDate(data.timestamp)?.getTime() || 0;
                }
              }
            } catch (error) {
              console.error(`Error fetching last activity for user ${userId}:`, error);
            }
          });
          
          await Promise.all(promises);
        }
      }
    } catch (error) {
      console.error('Error fetching users with activity:', error);
    }
    
    return userMap;
  }, []);

  // Function to fetch audit logs from Firestore
  const fetchAuditLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // First, fetch all users with their latest activity
      const allUsersMap = await fetchAllUsersWithLatestActivity();
      
      // Create the base query for logs from last 30 days
      const thirtyDaysAgo = subDays(new Date(), 30);
      const auditLogsRef = collection(db, 'audit_logs');
      let auditQuery = query(
        auditLogsRef,
        where('timestamp', '>=', Timestamp.fromDate(thirtyDaysAgo)),
        firestoreOrderBy('timestamp', orderDirection === 'asc' ? 'asc' : 'desc')
        // No limit - we want all logs from last 30 days
      );
      
      // Get all matching logs
      const snapshot = await getDocs(auditQuery);
      
      // Process the fetched logs
      const fetchedLogs = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          timestamp: toDate(data.timestamp)?.getTime() || Date.now(),
          action: data.action || 'Unknown Action',
          actionType: data.actionType || 'Unknown',
          userId: data.userId || 'unknown',
          userName: data.userName || 'Unknown',
          userRole: data.userRole || 'Unknown',
          details: data.details || '',
          clientId: data.clientId,
          clientName: data.clientName,
          metadata: data.metadata
        } as AuditLog;
      });
      
      // Store logs in state
      setLogs(fetchedLogs);
      
      // Apply any existing filters
      applyFilters(fetchedLogs);
      
      // Extract available filter options (include all users)
      extractFilterOptionsWithAllUsers(fetchedLogs, allUsersMap);
      
      // Calculate user summaries with all users included (with last activity data)
      calculateUserSummariesWithAllUsersAndActivity(fetchedLogs, allUsersMap);
      
      // Store in sessionStorage for persistence
      sessionStorage.setItem('auditLogs_data', JSON.stringify(fetchedLogs));
      sessionStorage.setItem('auditLogs_lastFetchTime', Date.now().toString());
      sessionStorage.setItem('auditLogs_allUsers', JSON.stringify(Array.from(allUsersMap.entries())));
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      setError('Failed to load audit logs. Please try again.');
      setLoading(false);
    }
  }, [setLoading, setError, orderDirection, extractFilterOptionsWithAllUsers, applyFilters, fetchAllUsersWithLatestActivity, calculateUserSummariesWithAllUsersAndActivity]);

  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Handle action type filter change
  const handleActionTypeChange = (e: SelectChangeEvent) => {
    setActionTypeFilter(e.target.value);
  };

  // Handle user name filter change
  const handleUserNameChange = (e: SelectChangeEvent) => {
    setUserNameFilter(e.target.value);
  };

  // Handle user role filter change
  const handleUserRoleChange = (e: SelectChangeEvent) => {
    setUserRoleFilter(e.target.value);
  };

  // Handle page change
  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // Handle summary sort request
  const handleSummarySort = (property: string) => {
    const isAsc = summaryOrderBy === property && summaryOrderDirection === 'asc';
    setSummaryOrderDirection(isAsc ? 'desc' : 'asc');
    setSummaryOrderBy(property);
  };
  
  // Export summary data
  const handleExportSummary = () => {
    const csvContent = [
      ['User ID', 'User Name', 'Role', 'Last Activity', 'Days Active', 'Total Actions'].join(','),
      ...userSummaries.map(summary => [
        summary.userId,
        summary.userName,
        summary.userRole,
        isValidDate(summary.lastActivity) ? format(new Date(summary.lastActivity), 'yyyy-MM-dd HH:mm:ss') : 'N/A',
        summary.daysWithActivity,
        summary.totalActions
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `user_activity_summary_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setSnackbar({
      open: true,
      message: 'Summary data exported successfully',
      type: 'success'
    });
  };

  // Handle apply filters
  const handleApplyFilters = () => {
    applyFilters(logs);
  };

  // Handle reset filters
  const handleResetFilters = () => {
    setSearchTerm('');
    setActionTypeFilter('all');
    setUserNameFilter('all');
    setUserRoleFilter('all');
    applyFilters(logs);
  };

  // Handle view details
  const handleViewDetails = (log: AuditLog) => {
    setSelectedLog(log);
    setShowDetails(true);
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchAuditLogs();
  };

  // Get color for action type chip
  const getActionTypeColor = (type: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (type) {
      case 'ACCESS':
        return 'info';
      case 'CREATE':
        return 'success';
      case 'UPDATE':
        return 'warning';
      case 'DELETE':
        return 'error';
      case 'LOGIN':
        return 'primary';
      case 'LOGOUT':
        return 'secondary';
      default:
        return 'default';
    }
  };

  // Initial data load - only runs once on component mount
  useEffect(() => {
    const initializeComponent = async () => {
      // Check if we have cached data in sessionStorage
      const cachedData = sessionStorage.getItem('auditLogs_data');
      const cachedAllUsers = sessionStorage.getItem('auditLogs_allUsers');
      const lastFetchTime = parseInt(sessionStorage.getItem('auditLogs_lastFetchTime') || '0', 10);
      const now = Date.now();
      const dataAge = now - lastFetchTime;
      const STALE_THRESHOLD = 5 * 60 * 1000; // 5 minutes
      
      if (cachedData && cachedAllUsers && dataAge < STALE_THRESHOLD) {
        // Use cached data if it's not stale
        try {
          const parsedData = JSON.parse(cachedData) as AuditLog[];
          const parsedAllUsers = new Map<string, { userId: string; userName: string; userRole: string; lastActivity: number }>(
            JSON.parse(cachedAllUsers)
          );
          setLogs(parsedData);
          
          // Restore filters from sessionStorage
          const cachedFilters = sessionStorage.getItem('auditLogs_filters');
          if (cachedFilters) {
            const { searchTerm, actionTypeFilter, userNameFilter, userRoleFilter } = JSON.parse(cachedFilters);
            setSearchTerm(searchTerm || '');
            setActionTypeFilter(actionTypeFilter || 'all');
            setUserNameFilter(userNameFilter || 'all');
            setUserRoleFilter(userRoleFilter || 'all');
          }
          
          // Extract filter options from the data (including all users)
          extractFilterOptionsWithAllUsers(parsedData, parsedAllUsers);
          
          // Calculate user summaries with all users and activity
          calculateUserSummariesWithAllUsersAndActivity(parsedData, parsedAllUsers);
          
          // We'll let the separate useEffect handle applying filters
          // to avoid dependency cycles
        } catch (error) {
          console.error('Error parsing cached data:', error);
          // If there's an error parsing cached data, fetch fresh data
          fetchAuditLogs();
        }
      } else {
        // Fetch fresh data if cache is stale or doesn't exist
        fetchAuditLogs();
      }
    };
    
    initializeComponent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array ensures this only runs once

  // Apply filters when logs or filter state changes
  // This is separate from the initialization to avoid dependency cycles
  useEffect(() => {
    if (logs.length > 0) {
      applyFilters(logs);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logs, searchTerm, actionTypeFilter, userNameFilter, userRoleFilter]);

  // Calculate paginated data
  const paginatedData = filteredLogs.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  
  // Sort and paginate summary data
  const sortedSummaries = [...userSummaries].sort((a, b) => {
    let aValue: any = a[summaryOrderBy as keyof UserActivitySummary];
    let bValue: any = b[summaryOrderBy as keyof UserActivitySummary];
    
    if (summaryOrderDirection === 'asc') {
      return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
    } else {
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    }
  });
  
  const paginatedSummaries = sortedSummaries.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', mb: 2, p: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="audit logs tabs">
            <Tab label="Audit Logs" value="logs" />
            <Tab label="Summary" value="summary" />
          </Tabs>
        </Box>
        
        {/* Filters Section - Only show for logs tab */}
        {activeTab === 'logs' && (
        <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ minWidth: 200 }}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1 }} />
            }}
          />
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="action-type-label">Action Type</InputLabel>
            <Select
              labelId="action-type-label"
              value={actionTypeFilter}
              label="Action Type"
              onChange={handleActionTypeChange}
            >
              {availableActionTypes.map(type => (
                <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="user-name-label">User</InputLabel>
            <Select
              labelId="user-name-label"
              value={userNameFilter}
              label="User"
              onChange={handleUserNameChange}
            >
              {availableUserNames.map(user => (
                <MenuItem key={user.id} value={user.id}>{user.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="user-role-label">Role</InputLabel>
            <Select
              labelId="user-role-label"
              value={userRoleFilter}
              label="Role"
              onChange={handleUserRoleChange}
            >
              {availableUserRoles.map(role => (
                <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Button 
            variant="contained" 
            startIcon={<FilterIcon />}
            onClick={handleApplyFilters}
            size="small"
          >
            Apply Filters
          </Button>
          
          <Button 
            variant="outlined" 
            startIcon={<ClearIcon />}
            onClick={handleResetFilters}
            size="small"
          >
            Reset
          </Button>
          
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            size="small"
          >
            Refresh
          </Button>
        </Box>
        )}
        
        {/* Content based on active tab */}
        {activeTab === 'logs' ? (
          <>
        {/* Audit Logs Table */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Details</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.length > 0 ? (
                paginatedData.map(log => (
                  <TableRow key={log.id} hover>
                    <TableCell>
                      <Typography variant="body2">
                        {isValidDate(log.timestamp) ? format(new Date(log.timestamp), 'MMM dd, yyyy') : 'Unknown date'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {isValidDate(log.timestamp) ? format(new Date(log.timestamp), 'HH:mm:ss') : 'Unknown time'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{log.action}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={log.actionType} 
                        size="small" 
                        color={getActionTypeColor(log.actionType)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{log.userName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{log.userRole}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {log.details}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {log.clientName ? (
                        <Typography variant="body2">{log.clientName}</Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">N/A</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleViewDetails(log)}>
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body1" sx={{ py: 2 }}>
                      No audit logs found. Try adjusting your filters or refreshing the data.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredLogs.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
        </>
        ) : (
          <>
        {/* Summary Section */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">User Activity Summary</Typography>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportSummary}
              size="small"
            >
              Export CSV
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Activity metrics show data from the last 30 days. Last activity dates include all historical data.
          </Typography>
        </Box>
        
        {/* Summary Stats */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4">{userSummaries.length}</Typography>
              <Typography variant="body2" color="text.secondary">Total Users</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4">{logs.length}</Typography>
              <Typography variant="body2" color="text.secondary">Total Actions (30 days)</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4">
                {userSummaries.filter(u => {
                  const daysSinceLastActivity = differenceInDays(new Date(), new Date(u.lastActivity));
                  return daysSinceLastActivity <= 7;
                }).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">Active Last 7 Days</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4">
                {userSummaries.length > 0 
                  ? Math.round(userSummaries.reduce((acc, u) => acc + u.daysWithActivity, 0) / userSummaries.length)
                  : 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">Avg Days Active (30 days)</Typography>
            </Paper>
          </Grid>
        </Grid>
        
        {/* Summary Table */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell 
                  onClick={() => handleSummarySort('userName')}
                  sx={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  User {summaryOrderBy === 'userName' && (summaryOrderDirection === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell>Role</TableCell>
                <TableCell 
                  onClick={() => handleSummarySort('lastActivity')}
                  sx={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  <Tooltip title="Shows the most recent activity date from all time, not just the last 30 days">
                    <span>Last Activity {summaryOrderBy === 'lastActivity' && (summaryOrderDirection === 'asc' ? '↑' : '↓')}</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="center"
                  onClick={() => handleSummarySort('daysWithActivity')}
                  sx={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  Days Active (30d) {summaryOrderBy === 'daysWithActivity' && (summaryOrderDirection === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell align="center"
                  onClick={() => handleSummarySort('totalActions')}
                  sx={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  Total Actions (30d) {summaryOrderBy === 'totalActions' && (summaryOrderDirection === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell>Top Actions (30d)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedSummaries.length > 0 ? (
                paginatedSummaries.map(summary => {
                  const daysSinceLastActivity = differenceInDays(new Date(), new Date(summary.lastActivity));
                  const isRecent = daysSinceLastActivity <= 7;
                  const topActionTypes = Object.entries(summary.actionsByType)
                    .sort(([, a], [, b]) => b - a)
                    .slice(0, 3);
                  
                  return (
                    <TableRow key={summary.userId} hover>
                      <TableCell>
                        <Typography variant="body2">{summary.userName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={summary.userRole} size="small" />
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0}>
                          <Typography variant="body2">
                            {isValidDate(summary.lastActivity) 
                              ? format(new Date(summary.lastActivity), 'MMM dd, yyyy')
                              : 'N/A'}
                          </Typography>
                          <Typography variant="caption" color={isRecent ? 'success.main' : 'text.secondary'}>
                            {daysSinceLastActivity === 0 ? 'Today' : 
                             daysSinceLastActivity === 1 ? 'Yesterday' :
                             `${daysSinceLastActivity} days ago`}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">{summary.daysWithActivity}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">{summary.totalActions}</Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          {topActionTypes.map(([type, count]) => (
                            <Tooltip key={type} title={`${count} ${type} actions`}>
                              <Chip 
                                label={`${type} (${count})`} 
                                size="small" 
                                color={getActionTypeColor(type)}
                              />
                            </Tooltip>
                          ))}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body1" sx={{ py: 2 }}>
                      No user activity data available.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Summary Pagination */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={userSummaries.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
        </>
        )}
      </Paper>
      
      {/* Log Details Dialog */}
      <Dialog open={showDetails} onClose={() => setShowDetails(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Audit Log Details
          <IconButton
            aria-label="close"
            onClick={() => setShowDetails(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <ClearIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedLog && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2">Action</Typography>
                <Typography variant="body2">{selectedLog.action}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2">Action Type</Typography>
                <Chip 
                  label={selectedLog.actionType} 
                  size="small" 
                  color={getActionTypeColor(selectedLog.actionType)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2">Date & Time</Typography>
                <Typography variant="body2">
                  {selectedLog?.timestamp && isValidDate(selectedLog.timestamp) 
                    ? format(new Date(selectedLog.timestamp), 'dd MMM yyyy HH:mm:ss') 
                    : 'Unknown date/time'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2">User</Typography>
                <Typography variant="body2">{selectedLog.userName} ({selectedLog.userRole})</Typography>
                <Typography variant="caption" color="text.secondary">ID: {selectedLog.userId}</Typography>
              </Grid>
              {selectedLog.clientId && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Client</Typography>
                  <Typography variant="body2">{selectedLog.clientName}</Typography>
                  <Typography variant="caption" color="text.secondary">ID: {selectedLog.clientId}</Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="subtitle2">Details</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedLog.details}
                </Typography>
              </Grid>
              {selectedLog.metadata && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Additional Metadata</Typography>
                  <Box component="pre" sx={{ 
                    bgcolor: 'grey.100', 
                    p: 1, 
                    borderRadius: 1,
                    overflow: 'auto',
                    fontSize: '0.875rem'
                  }}>
                    {JSON.stringify(selectedLog.metadata, null, 2)}
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetails(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditLogs;
